import { translations } from './translations';
import { useDispatch, useSelector } from '~Hooks';
import { Locale } from '~Types';
import { useStorage } from '~/Components/Storage';
import * as actions from '../actions';
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/sv';
import 'moment/locale/nb';
import 'moment/locale/en-gb';
import { useCallback } from 'react';

const LOCALE_MOMENT: Record<Locale, string> = {
  se: 'sv-SE',
  en: 'en-GB',
  no: 'nb-NO',
  es: 'es-ES',
  dk: 'da-DK',
  tr: 'tr-TR',
};

export type i18nProps = (obj?: any | null | string, ...params: unknown[]) => string;

type useI18nProps = (
  fixedLanguage?: Locale, // Optional fixed language to ensure that the language is not adjusted based on what language OM is set to
) => {
  i18n: i18nProps;
  locale: Locale;
  validate: (key: string) => boolean;
  changeLanguage: (locale: string) => void;
};

const useI18n: useI18nProps = (fixedLanguage) => {
  const dispatch = useDispatch();
  const [{ selectedLanguage }, setStorage] = useStorage();
  const locale = useSelector((state) => state.locale);

  /*
   * fixedLanguage: Language has been predefined when initializing the hook
   * selectedLanguage: Language has been selected before and is stored in the storage
   * locale: Language that the company has set in backoffice
   */
  const languageToUse = fixedLanguage || (selectedLanguage as Locale) || locale;

  const getTranslate = useCallback(
    (translatedString: any) => {
      if (languageToUse && translatedString[languageToUse]) {
        return translatedString[languageToUse];
      }

      const keys = Object.keys(translatedString);
      for (let i = 0; i < keys.length; i += 1) {
        if (translatedString[keys[i]] && keys[i] !== 'id' && keys[i] !== '__typename') {
          return translatedString[keys[i]];
        }
      }
      return '';
    },
    [languageToUse],
  );

  const i18n: i18nProps = useCallback(
    (obj, ...params) => {
      if (!obj || !languageToUse) {
        return '';
      }

      if (typeof obj === 'object') {
        return getTranslate(obj);
      }

      if (!translations[obj]) {
        return obj;
      }

      const foundTranslation = translations[obj][languageToUse];

      // Guard for missing translations
      if (!foundTranslation) {
        console.log('Missing translation', obj);
        return `Missing translation: [${obj}]`;
      }

      // Handle found translation
      switch (typeof foundTranslation) {
        case 'function':
          return foundTranslation(params);
        case 'string':
        default:
          return foundTranslation;
      }
    },
    [languageToUse, getTranslate],
  );

  const validate = useCallback((key: string) => {
    return Boolean(translations[key]);
  }, []);

  const changeLanguage = useCallback(
    (localeInput: string) => {
      if (LOCALE_MOMENT[localeInput as Locale]) {
        moment.locale(LOCALE_MOMENT[localeInput as Locale]);
      }
      // @ts-ignore
      dispatch(actions.changeLocale(localeInput));
      setStorage({ selectedLanguage: localeInput });
    },
    [dispatch, setStorage],
  );

  return { i18n, locale: languageToUse, validate, changeLanguage };
};

export default useI18n;
